'use client';

import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { getAvatarColor, getInitials, hasProfessionalSchedule } from '../utils';
import { Professional } from '@/containers/CustomerJourneySetup/context/types';
import { useWindowResize } from '@/hooks';

interface ProfessionalSelectorProps {
  professionals: Professional[];
  selectedProfessionalId: string;
  onSelectProfessional: (id: string) => void;
}

export const StaffSelector = ({
  professionals,
  selectedProfessionalId,
  onSelectProfessional
}: ProfessionalSelectorProps) => {
  const [isSticky, setIsSticky] = useState(false);
  const { isMobileSize } = useWindowResize();

  useEffect(() => {
    if (!isMobileSize) return;
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsSticky(scrollTop > 200);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobileSize]);

  const cardVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    hover: {
      boxShadow: '0 4px 12px rgba(0,114,251,0.15)',
      transition: { duration: 0.2 }
    }
  };

  const mobileCardVariants = {
    initial: { opacity: 0, scale: 0.9 },
    animate: { opacity: 1, scale: 1 },
    tap: { scale: 0.95 }
  };

  const MobileView = () => (
    <div
      className={`md:hidden transition-all duration-300 ${
        isSticky
          ? 'fixed z-[9999] top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-md'
          : 'relative bg-white max-[600px]:max-w-[calc(100vw-60px)] scrollbar-hide'
      }`}
    >
      <div className={isSticky ? 'px-4 py-3' : ''}>
        <div className="flex gap-3 pb-2 overflow-x-auto scrollbar-hide">
          {professionals.map((professional, index) => {
            const hasSchedule = hasProfessionalSchedule(professional);
            const avatarColor = getAvatarColor(professional.id);
            const isSelected = selectedProfessionalId === professional.id;
            const initials = getInitials(`${professional.first_name} ${professional.last_name}`);

            return (
              <motion.div
                key={professional.id}
                variants={mobileCardVariants}
                initial="initial"
                animate="animate"
                whileTap="tap"
                transition={{ duration: 0.2, delay: index * 0.02 }}
                className="flex-shrink-0"
              >
                <button
                  onClick={() => onSelectProfessional(professional.id)}
                  className={`relative flex flex-col items-center gap-1.5 rounded-lg border p-2.5 min-w-[70px] transition-all duration-200 ${
                    isSelected
                      ? 'border-blue bg-blue/10 shadow-sm'
                      : hasSchedule
                      ? 'bg-green-50/70 border-green-200'
                      : 'border-gray-200 bg-white'
                  }`}
                  aria-label={`Seleccionar ${professional.first_name} ${professional.last_name}`}
                >
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium text-white ${avatarColor} ${
                      isSelected ? 'ring-2 ring-blue ring-offset-1' : ''
                    } ${
                      hasSchedule && !isSelected ? 'ring-1 ring-green-400' : ''
                    } transition-all duration-200`}
                  >
                    {initials}
                  </div>

                  <div className="flex flex-col items-center text-center">
                    <span className="text-xs font-medium text-gray-800 line-clamp-1 leading-tight max-w-[60px]">
                      {professional.first_name}
                    </span>
                    <div
                      className={`w-1 h-1 rounded-full mt-0.5 ${
                        hasSchedule ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    />
                  </div>
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
      {isSticky && <div className="h-px bg-gray-200" />}
    </div>
  );

  const DesktopView = () => (
    <div className={`hidden md:flex flex-col gap-4 transition-all duration-300 sticky top-4`}>
      {professionals.map((professional, index) => {
        const hasSchedule = hasProfessionalSchedule(professional);
        const avatarColor = getAvatarColor(professional.id);
        const isSelected = selectedProfessionalId === professional.id;
        const initials = getInitials(`${professional.first_name} ${professional.last_name}`);

        return (
          <motion.div
            key={professional.id}
            variants={cardVariants}
            initial="initial"
            animate="animate"
            whileHover="hover"
            transition={{ duration: 0.3, delay: index * 0.05 }}
            className="w-full rounded-xl"
          >
            <button
              onClick={() => onSelectProfessional(professional.id)}
              className={`relative flex w-full items-center gap-3 rounded-xl border px-3 py-2 min-h-[56px] transition-all duration-200 ${
                isSelected ? 'border-blue bg-blue/5 shadow-md' : ''
              } ${hasSchedule && !isSelected ? 'bg-green-50/50 border-green-100' : ''} ${
                !hasSchedule && !isSelected ? 'border-gray-200 bg-white' : ''
              }`}
              aria-label={`Seleccionar ${professional.first_name} ${professional.last_name}`}
            >
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-full text-base font-medium text-white ${avatarColor} shadow-inner mx-0 transition-all duration-300 ${
                  isSelected ? 'ring-2 ring-blue ring-offset-2' : ''
                } ${hasSchedule && !isSelected ? 'ring-1 ring-green-500 ring-offset-1' : ''}`}
              >
                {initials}
              </div>

              <div className="flex-1 flex flex-col items-start justify-center min-w-0">
                <span className="text-sm font-medium text-gray-800 truncate max-w-[120px]">
                  {professional.first_name} {professional.last_name}
                </span>
                {hasSchedule ? (
                  <span className="mt-1 text-[0.65rem] bg-green-100 text-green-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                    Con horarios
                  </span>
                ) : (
                  <span className="mt-1 text-[0.65rem] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full whitespace-nowrap">
                    Sin horarios
                  </span>
                )}
              </div>

              {isSelected && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="absolute -top-1 -right-1 bg-blue text-white rounded-full w-5 h-5 flex items-center justify-center"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 6L9 17L4 12"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </motion.div>
              )}
            </button>
          </motion.div>
        );
      })}
    </div>
  );

  return (
    <>
      {isMobileSize ? <MobileView /> : <DesktopView />}
      {isSticky && <div className="md:hidden h-[88px]" />}
    </>
  );
};
